import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { Alert, Box, Center, Loader } from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import type { WordBox } from "../../api/types";
import { highlightPlain, highlightWords } from "../../lib/snippet";

// Must be set in the module that renders <Document> (react-pdf docs).
// Legacy build (polyfilled, Chrome 125+ / Firefox ESR / Safari 18): the modern build needs
// Map.prototype.getOrInsertComputed and breaks on corporate browsers a year behind.
// The main-thread bundle is switched to legacy by the alias in vite.config.ts.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/legacy/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

interface Props {
  fileUrl: string;
  pageNo: number;
  width: number;
  scale: number;
  highlightTerms: readonly string[];
  /** OCR word boxes of the current page (normalized 0..1) for scanned pages. */
  words: WordBox[] | null;
  onLoaded(numPages: number): void;
  onPageRendered?(): void;
}

/** One page of the PDF with query terms marked in the text layer or as boxes over OCR words. */
export function PdfView({
  fileUrl,
  pageNo,
  width,
  scale,
  highlightTerms,
  words,
  onLoaded,
  onPageRendered,
}: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  // A failed load must not stick to the next document while this component stays mounted.
  useEffect(() => setError(null), [fileUrl]);
  const terms = useMemo(() => highlightWords(highlightTerms), [highlightTerms]);
  // react-pdf parses the returned string as HTML, so the text is escaped even with no terms:
  // document text containing "<" must not become markup (or an outbound <img> request).
  const textRenderer = useCallback(
    ({ str }: { str: string }) => highlightPlain(str, terms),
    [terms],
  );
  const boxes = useMemo(() => {
    if (!words || terms.length === 0) return [];
    return words.filter(([w]) => {
      const lw = w.toLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
      return lw.length > 1 && terms.some((t) => lw === t || lw.startsWith(t));
    });
  }, [words, terms]);
  const pageWidth = Math.max(200, Math.floor(width * scale));

  if (error) {
    return (
      <Alert color="red" variant="light">
        {error}
      </Alert>
    );
  }
  return (
    <Document
      file={fileUrl}
      onLoadSuccess={(doc) => onLoaded(doc.numPages)}
      onLoadError={(e) =>
        setError(/\b409\b/.test(e.message) ? t("sources.fileUnavailable") : e.message)
      }
      loading={
        <Center h={300}>
          <Loader />
        </Center>
      }
    >
      <Box pos="relative" style={{ width: pageWidth, margin: "0 auto" }}>
        <Page
          pageNumber={pageNo}
          width={pageWidth}
          customTextRenderer={textRenderer}
          renderAnnotationLayer={false}
          {...(onPageRendered ? { onRenderSuccess: onPageRendered } : {})}
          loading={
            <Center h={300}>
              <Loader />
            </Center>
          }
        />
        {boxes.map(([w, x0, y0, x1, y1], i) => (
          <Box
            key={`${w}-${i}`}
            pos="absolute"
            style={{
              left: `${x0 * 100}%`,
              top: `${y0 * 100}%`,
              width: `${(x1 - x0) * 100}%`,
              height: `${(y1 - y0) * 100}%`,
              background: "rgba(255, 213, 0, 0.45)",
              borderRadius: 2,
              pointerEvents: "none",
            }}
          />
        ))}
      </Box>
    </Document>
  );
}
